import { Component, OnInit, Input, Self, Optional, ViewChild, Output, EventEmitter } from '@angular/core';
import { NgControl, ControlValueAccessor } from '@angular/forms';
import * as uuid from 'uuid';

@Component({
    selector: 'app-form-textarea',
    templateUrl: './textarea.component.html',
    styleUrls: ['./textarea.component.scss'],
})
export class FormTextareaComponent implements OnInit, ControlValueAccessor {
    public onChange: Function;

    public onTouched: Function;

    public id: string;

    public _value: any = '';

    @Input() disabled: boolean;

    @Input() label: string;

    /** Optional helper text rendered as a subtitle directly under the label. */
    @Input() subtitle: string;

    @Input() placeholder: string;

    @Input() rows = 3;

    @Input() validation = true;

    @Input() disableMessages = false;

    @Input() hint: string;

    @Input() charLimit: number;

    @Input() wordLimit: number;

    /** Exposes the field as required to assistive technology; validation stays on the form control. */
    @Input() required = false;

    @Output('change') changeOutput = new EventEmitter<any>();

    @Output() blur = new EventEmitter<Event>();

    @ViewChild('input', { static: true }) input;

    constructor(@Self() @Optional() private ngControl: NgControl) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    get currentCharactersAmount(): number {
        return this._value?.length ?? 0;
    }

    get currentWordCount(): number {
        if (!this._value) return 0;
        return this._value
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0).length;
    }

    get hasMaxLengthError(): boolean {
        return this.ngControl.getError('maxlength');
    }

    get hasMaxWordError(): boolean {
        return this.wordLimit && this.currentWordCount > this.wordLimit;
    }

    get invalid() {
        if (this.ngControl) {
            return this.ngControl.invalid;
        }
        return false;
    }

    get valid() {
        if (this.ngControl) {
            return this.ngControl.valid;
        }
        return false;
    }

    get pristine() {
        if (this.ngControl) {
            return this.ngControl.pristine;
        }
        return false;
    }

    get dirty() {
        if (this.ngControl) {
            return this.ngControl.dirty;
        }
        return false;
    }

    get touched() {
        if (this.ngControl) {
            return this.ngControl.touched;
        }
        return false;
    }

    ngOnInit(): void {
        this.id = uuid.v4();
    }

    get value(): any {
        return this._value;
    }

    set value(v) {
        this._value = v;
        if (this.onChange) {
            this.changeOutput.emit(v);
            this.onChange(v);
            this.onTouched();
        }
    }

    valueChange(v) {
        this.value = v;
    }

    onBlur(event: Event) {
        this.blur.emit(event);
        this.onTouched();
    }

    writeValue(value: any): void {
        this._value = value;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    registerOnChange(fn: any): void {
        // Store the provided function as an internal method.
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        // Store the provided function as an internal method.
        this.onTouched = fn;
    }
}
